import BaseSetup from '@/components/BaseSetup';
import { useCompanyAgencyService } from '@/services/company/CompanyAgencyService';
import { useCompanyClientService } from '@/services/company/CompanyClientService';
import { useCompanyService } from '@/services/company/CompanyService';
import { Reactive } from '@/utils/decorators';
import { watch } from 'vue';

export type SharedSelectUserCompanyEmits = {
	(e: 'update:modelValue' | 'input', id: number | number[] | undefined): void
	(e: 'update:selected', companyModel?: ICompanyModel | ICompanyModel[]): void
	(e: 'select', companyModel: ICompanyModel | ICompanyModel[]): void
};

export type SharedSelectUserCompanyProps = {
	hideAgencyName?: boolean
	setToFirst?: boolean
	autoSelectSingle?: boolean
	displayValidation?: boolean
	organizationOnly?: boolean
	agency?: boolean
	client?: boolean
	agencyCompanyId?: number
	clearable?: boolean
	modelValue?: number | number[]
	options?: ICompanyModel[]
	multiple?: boolean
	admin?: boolean
	permissionAction?: number
	checkOrderPlugin?: boolean
};

type CompanyElement = ICompanyWithCheckPluginOrderModel & {
	disabled: boolean
};

export default class SharedSelectUserCompanySetup extends BaseSetup {

	static readonly defaultProps = { clearable: true };

	@Reactive private readonly state = { companies: [] as Partial<CompanyElement>[] };

	get searchable () { return this.companies.length >= 10 }
	get selectOptions () {
		if (this.props.options) return this.props.options;
		else return this.companies;
	}

	get publicInstance () {
		return { getOptions: () => this.selectOptions as ICompanyWithCheckPluginOrderModel[] };
	}

	get vModel () { return this.props.modelValue }
	set vModel (val) { this.emits('update:modelValue', val) }

	get companies () { return this.state.companies }
	set companies (val) { this.state.companies = val }

	constructor (
		private props: SharedSelectUserCompanyProps,
		private emits: SharedSelectUserCompanyEmits,
	) {
		super();

		watch(() => this.props.agencyCompanyId, (val) => {
			if (val) this.vModel = undefined;
			this.getCompaniesAsync();
		});

		watch(() => this.props.modelValue, (val) => {
			if (typeof val === 'number') {
				this.emits('update:selected', this.companies.findByKey(val, 'id') as Undef<ICompanyModel>);
			}
			else {
				this.emits('update:selected', this.companies.filter(x => x.id && val?.includes(x.id)) as Undef<ICompanyModel[]>);
			}
		});
	}

	async onBeforeMountAsync () {
		await this.getCompaniesAsync(true);
	}

	computedLabel () {
		if (this.props.agency) return this.$t('common.label.agency');
		else if (this.props.client) return this.$t('common.label.company');
		else return this.self.isClient() ? this.$t('common.label.company') : this.$t('common.label.agency');
	}

	private handleAutoSelect () {
		if (this.selectOptions?.length) {
			const firstCompany = this.selectOptions.first() as CompanyElement;
			if (firstCompany && !this.props.modelValue
			  && (this.props.setToFirst || (this.props.autoSelectSingle && this.selectOptions.length === 1)) && !firstCompany.disabled
			) {
				if (this.props.multiple) {
					this.emits('input', [firstCompany.id]);
					this.emits('update:modelValue', [firstCompany.id]);
					this.emits('update:selected', [firstCompany]);
					this.emits('select', [firstCompany]);
				}
				else {
					this.emits('input', firstCompany.id);
					this.emits('update:modelValue', firstCompany.id);
					this.emits('update:selected', firstCompany);
					this.emits('select', firstCompany);
				}
			}
		}
	}

	private async getCompaniesAsync (autoSelect = false) {
		const { permissionAction, agencyCompanyId, checkOrderPlugin = false } = this.props;

		if ((!this.props.agency && !this.props.client)
		  || (this.self.isAgency() && this.props.agency)
		  || (this.self.isClient() && this.props.client && !this.props.agencyCompanyId)
		  || this.self.isTempWorker()
		) {
			const data = await useCompanyService().getAsync({
				permissionAction,
				checkOrderPlugin,
				organizationId: this.props.organizationOnly ? this.self.organization?.id : undefined,
				agencyCompanyId: this.props.agencyCompanyId,
			});

			(data as CompanyElement[]).forEach((element) => {
				element.disabled = element.canCreateOrder !== undefined ? !element.canCreateOrder : false;
			});
			this.companies = data;
		}
		else if (this.self.isClient()) {
			if (this.props.agency) {
				const data = await useCompanyClientService().getAgencyListAsync(permissionAction, checkOrderPlugin);
				(data as CompanyElement[]).forEach((element) => {
					element.disabled = element.canCreateOrder !== undefined ? !element.canCreateOrder : false;
				});
				this.companies = data;
			}
			else if (this.props.client) {
				const data = await useCompanyService().getAsync({
					agencyCompanyId,
					permissionAction,
					checkOrderPlugin,
				});
				this.companies = data;
			}
		}
		else if (this.self.isAgency() && this.props.client) {
			const data = await useCompanyAgencyService().clientListAsync({
				agencyCompanyId,
				checkOrderPlugin: false,
			});
			this.companies = data;
		}
		this.companies.sort(function (a, b) {
			if (a.name && b.name) {
				if (a.name < b.name) return -1;
				if (a.name > b.name) return 1;
			}
			return 0;
		});

		if (autoSelect) {
			this.handleAutoSelect();
		}
	}

}
